<template>
  <div class="image-view" v-loading="loading">
    <div class="image-view-container">
      <div class="image-tools">
        <div class="image-tools-main">
          <div class="document-heading">
            <span class="document-kicker">{{ previewKicker }}</span>
            <div class="title" :title="title">{{ title || '翻译结果查看' }}</div>
          </div>
          <div class="language" v-if="language">
            <i class="el-icon-connection"></i>
            <span>{{ language }}</span>
          </div>
          <div class="zoom-tools" :aria-label="previewMode === 'docx' ? '文档缩放' : '图片缩放'">
            <button
              type="button"
              :disabled="zoom <= minZoom"
              :data-track-text="previewMode === 'docx' ? '缩小Word文档' : '缩小图片'"
              :aria-label="previewMode === 'docx' ? '缩小Word文档' : '缩小图片'"
              @click="changeZoom(-zoomStep)"
            >
              <i class="el-icon-minus"></i>
            </button>
            <button
              type="button"
              class="zoom-value"
              :data-track-text="previewMode === 'docx' ? '恢复Word文档默认大小' : '恢复图片默认大小'"
              :aria-label="previewMode === 'docx' ? '恢复Word文档默认大小' : '恢复图片默认大小'"
              @click="resetZoom"
            >{{ zoom }}%</button>
            <button
              type="button"
              :disabled="zoom >= maxZoom"
              :data-track-text="previewMode === 'docx' ? '放大Word文档' : '放大图片'"
              :aria-label="previewMode === 'docx' ? '放大Word文档' : '放大图片'"
              @click="changeZoom(zoomStep)"
            >
              <i class="el-icon-plus"></i>
            </button>
          </div>
          <button
            type="button"
            class="download"
            :disabled="!translateUrl || downloading"
            :data-track-text="downloadLabel"
            :aria-label="downloadLabel"
            @click="downloadTranslatedFile"
          >
            <i :class="downloading ? 'el-icon-loading' : 'el-icon-download'"></i>
            <span>{{ downloadLabel }}</span>
          </button>
        </div>

        <div class="mobile-pane-switch" role="tablist" aria-label="图片内容切换">
          <button
            type="button"
            role="tab"
            :aria-selected="mobilePane === 'origin'"
            :class="{ active: mobilePane === 'origin' }"
            data-track-text="移动端查看原图"
            @click="mobilePane = 'origin'"
          >原图</button>
          <button
            type="button"
            role="tab"
            :aria-selected="mobilePane === 'translate'"
            :class="{ active: mobilePane === 'translate' }"
            :data-track-text="previewMode === 'docx' ? '移动端查看Word译文' : '移动端查看译图'"
            @click="mobilePane = 'translate'"
          >{{ translatedPaneLabel }}</button>
        </div>
      </div>

      <div v-if="errorMessage" class="preview-error">
        <i class="el-icon-warning-outline"></i>
        <strong>{{ previewMode === 'docx' ? 'Word译文暂时无法查看' : '图片暂时无法查看' }}</strong>
        <span>{{ errorMessage }}</span>
        <el-button type="primary" size="small" @click="loadDocument">重新加载</el-button>
      </div>

      <div v-else class="image-container">
        <section
          class="image-pane image-origin"
          v-show="!isMobile || mobilePane === 'origin'"
        >
          <div class="pane-label"><i class="el-icon-picture-outline"></i> 原图</div>
          <el-scrollbar class="image-scrollbar">
            <div class="image-stage">
              <img
                v-if="originalUrl"
                :src="originalUrl"
                :alt="title ? title + '原图' : '翻译原图'"
                :style="imageStyle"
                @load="markImageLoaded('origin')"
                @error="markImageError('原图加载失败')"
              />
            </div>
          </el-scrollbar>
        </section>

        <section
          class="image-pane image-translate"
          v-show="!isMobile || mobilePane === 'translate'"
        >
          <div class="pane-label">
            <i :class="previewMode === 'docx' ? 'el-icon-document' : 'el-icon-picture'"></i>
            {{ translatedPaneLabel }}
          </div>
          <el-scrollbar class="image-scrollbar">
            <div v-if="previewMode === 'image'" class="image-stage">
              <img
                v-if="translateUrl"
                :src="translateUrl"
                :alt="title ? title + '译图' : '翻译结果图'"
                :style="imageStyle"
                @load="markImageLoaded('translate')"
                @error="markImageError('译图加载失败')"
              />
            </div>
            <div v-else class="docx-stage">
              <div ref="docxContainer" class="docx-preview-container" :style="docxStyle"></div>
            </div>
          </el-scrollbar>
        </section>
      </div>
    </div>
  </div>
</template>

<script>
import { renderAsync } from 'docx-preview/dist/docx-preview.js'
import { translateServiceClient } from '@/tools/api/service'
import { documentRequest } from '@/tools/api/translate-api'
import languages from '../../../../language.json'

export default {
  name: 'ImageView',
  props: {
    isShowMenu: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      title: '',
      language: '',
      originalUrl: '',
      translateUrl: '',
      previewMode: 'image',
      loading: true,
      downloading: false,
      errorMessage: '',
      loadedImages: {
        origin: false,
        translate: false
      },
      zoom: 100,
      minZoom: 50,
      maxZoom: 200,
      zoomStep: 25,
      isMobile: false,
      mobilePane: 'origin',
      resizeTimer: null
    }
  },
  computed: {
    previewKicker () {
      return this.previewMode === 'docx' ? '图片转Word在线查看' : '图片翻译对照'
    },
    translatedPaneLabel () {
      return this.previewMode === 'docx' ? 'Word译文' : '译图'
    },
    downloadLabel () {
      return this.previewMode === 'docx' ? '下载Word' : '下载译图'
    },
    imageStyle () {
      return {
        width: this.zoom + '%'
      }
    },
    docxStyle () {
      return {
        zoom: this.zoom / 100
      }
    }
  },
  watch: {
    isShowMenu () {
      this.resetZoom()
    }
  },
  mounted () {
    this.syncViewportMode()
    window.addEventListener('resize', this.handleResize)
    this.loadDocument()
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.handleResize)
    clearTimeout(this.resizeTimer)
  },
  methods: {
    syncViewportMode () {
      this.isMobile = window.matchMedia('(max-width: 768px)').matches
    },
    handleResize () {
      clearTimeout(this.resizeTimer)
      this.resizeTimer = setTimeout(this.syncViewportMode, 120)
    },
    getLanguageName (value) {
      const language = languages.find((item) => item.value === value)
      return language ? language.label : value
    },
    loadDocument () {
      this.loading = true
      this.errorMessage = ''
      this.originalUrl = ''
      this.translateUrl = ''
      this.previewMode = 'image'
      this.loadedImages = { origin: false, translate: false }
      documentRequest.setDocId(this.$route.params.documentId)
      translateServiceClient
        .getDocument(documentRequest, {
          token: localStorage.getItem('token')
        })
        .then((res) => {
          const originalUrl = res.getOriginalUrl()
          const translateUrl = res.getTranslateUrl()
          const responseMessage = res.getMessage()
          this.previewMode = responseMessage === 'DOCX_PREVIEW' || /Word/i.test(responseMessage)
            ? 'docx'
            : 'image'
          if (!originalUrl || !translateUrl) {
            this.errorMessage = responseMessage || '图片地址尚未准备完成，请稍后重试'
            this.loading = false
            return
          }
          this.originalUrl = originalUrl
          this.translateUrl = translateUrl
          this.title = res.getTitle()
          const from = this.getLanguageName(res.getLanguageFrom())
          const to = this.getLanguageName(res.getLanguageTo())
          this.language = from && to ? '语言：' + from + '→' + to : ''
          if (this.previewMode === 'docx') {
            this.$nextTick(this.renderDocx)
          }
        })
        .catch((err) => {
          this.loading = false
          this.errorMessage = err && err.code === 5
            ? '登录状态已失效，请重新登录'
            : '图片加载失败，请稍后重试'
        })
    },
    markImageLoaded (type) {
      this.$set(this.loadedImages, type, true)
      if (this.loadedImages.origin && this.loadedImages.translate) {
        this.loading = false
      }
    },
    markImageError (message) {
      this.loading = false
      this.errorMessage = message + '，请稍后重试'
    },
    async renderDocx () {
      const container = this.$refs.docxContainer
      if (!container || !this.translateUrl) return
      try {
        const response = await fetch(this.translateUrl, { credentials: 'omit' })
        if (!response.ok) throw new Error('docx fetch failed')
        const content = await response.arrayBuffer()
        await renderAsync(content, container, container, {
          breakPages: true,
          ignoreLastRenderedPageBreak: false,
          renderHeaders: true,
          renderFooters: true,
          renderFootnotes: true,
          useBase64URL: true
        })
        this.markImageLoaded('translate')
      } catch (err) {
        this.markImageError('Word译文加载失败')
      }
    },
    changeZoom (delta) {
      this.zoom = Math.max(this.minZoom, Math.min(this.maxZoom, this.zoom + delta))
    },
    resetZoom () {
      this.zoom = 100
    },
    translatedFileName () {
      const name = this.title || (this.previewMode === 'docx' ? 'translated-document.docx' : 'translated-image.png')
      const dotIndex = name.lastIndexOf('.')
      if (this.previewMode === 'docx') {
        return (dotIndex < 0 ? name : name.slice(0, dotIndex)) + '-译文.docx'
      }
      if (dotIndex < 0) return name + '-译图.png'
      return name.slice(0, dotIndex) + '-译图' + name.slice(dotIndex)
    },
    async downloadTranslatedFile () {
      if (!this.translateUrl || this.downloading) return
      this.downloading = true
      try {
        const response = await fetch(this.translateUrl)
        if (!response.ok) throw new Error('download failed')
        const blob = await response.blob()
        const objectUrl = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = objectUrl
        link.download = this.translatedFileName()
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(objectUrl)
      } catch (err) {
        window.open(this.translateUrl, '_blank', 'noopener')
        this.$message.info(this.previewMode === 'docx'
          ? '已在新窗口打开Word译文'
          : '已在新窗口打开译图，可使用浏览器保存图片')
      } finally {
        this.downloading = false
      }
    }
  }
}
</script>

<style lang="less" scoped>
.image-view {
  height: 100%;
  min-height: 0;
}

.image-view-container {
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #fff;
  border: 1px solid #e3eceb;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(31, 59, 52, 0.06);
}

.image-tools {
  flex: 0 0 auto;
  padding: 14px 18px;
  border-bottom: 1px solid #e8efed;
  background: #fff;
}

.image-tools-main {
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 14px;
}

.document-heading {
  min-width: 0;
  flex: 1;
}

.document-kicker {
  display: block;
  color: var(--primary);
  font-size: 12px;
  line-height: 18px;
}

.title {
  overflow: hidden;
  color: #263833;
  font-size: 18px;
  font-weight: 600;
  line-height: 26px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.language {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #60726d;
  font-size: 14px;
  white-space: nowrap;
}

.zoom-tools {
  display: inline-flex;
  align-items: center;
  overflow: hidden;
  border: 1px solid #dce7e4;
  border-radius: 8px;
}

.zoom-tools button,
.download {
  height: 34px;
  border: 0;
  color: #41534e;
  background: #fff;
  cursor: pointer;
}

.zoom-tools button {
  min-width: 36px;
  border-right: 1px solid #e3ece9;
}

.zoom-tools button:last-child {
  border-right: 0;
}

.zoom-tools button:disabled,
.download:disabled {
  color: #aebbb7;
  cursor: not-allowed;
}

.zoom-tools .zoom-value {
  min-width: 58px;
  color: var(--primary-dark);
  font-size: 13px;
}

.download {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 0 15px;
  color: #fff;
  border-radius: 8px;
  background: var(--primary);
}

.mobile-pane-switch {
  display: none;
}

.image-container {
  min-height: 0;
  flex: 1;
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 1px;
  background: #dfe9e6;
}

.image-pane {
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: #f7faf9;
}

.pane-label {
  flex: 0 0 42px;
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 0 16px;
  color: #50635d;
  border-bottom: 1px solid #e4ecea;
  background: #fff;
  font-size: 14px;
  font-weight: 500;
}

.pane-label i {
  color: var(--primary);
}

.image-scrollbar {
  min-height: 0;
  flex: 1;
}

.image-scrollbar /deep/ .el-scrollbar__wrap {
  overflow-x: auto;
}

.image-stage {
  min-width: 100%;
  min-height: 100%;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  box-sizing: border-box;
  padding: 24px;
  background-image: linear-gradient(45deg, #eef3f1 25%, transparent 25%),
    linear-gradient(-45deg, #eef3f1 25%, transparent 25%),
    linear-gradient(45deg, transparent 75%, #eef3f1 75%),
    linear-gradient(-45deg, transparent 75%, #eef3f1 75%);
  background-position: 0 0, 0 10px, 10px -10px, -10px 0;
  background-size: 20px 20px;
}

.image-stage img {
  display: block;
  max-width: none;
  height: auto;
  object-fit: contain;
  border-radius: 4px;
  background: #fff;
  box-shadow: 0 6px 18px rgba(25, 52, 45, 0.12);
  transition: width 0.18s ease;
}

.docx-stage {
  min-width: 100%;
  min-height: 100%;
  box-sizing: border-box;
  padding: 24px;
  overflow: auto;
  background: #e9eeec;
}

.docx-preview-container {
  min-width: fit-content;
  transform-origin: top center;
  transition: zoom 0.18s ease;
}

.docx-preview-container /deep/ .docx-wrapper {
  min-height: 100%;
  padding: 24px !important;
  background: transparent;
}

.docx-preview-container /deep/ .docx-wrapper > section.docx {
  margin: 0 auto 20px;
  box-shadow: 0 6px 18px rgba(25, 52, 45, 0.14);
}

.preview-error {
  min-height: 0;
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  color: #75827f;
}

.preview-error i {
  color: #e6a23c;
  font-size: 38px;
}

.preview-error strong {
  color: #354640;
  font-size: 18px;
}

@media (max-width: 900px) {
  .language {
    display: none;
  }
}

@media (max-width: 768px) {
  .image-view-container {
    border-radius: 9px;
  }

  .image-tools {
    padding: 10px 12px 0;
  }

  .image-tools-main {
    gap: 8px;
  }

  .document-kicker,
  .zoom-tools {
    display: none;
  }

  .title {
    font-size: 15px;
    line-height: 34px;
  }

  .download {
    height: 32px;
    padding: 0 11px;
  }

  .download span {
    display: none;
  }

  .mobile-pane-switch {
    display: grid;
    grid-template-columns: 1fr 1fr;
    margin-top: 8px;
    border-top: 1px solid #eef3f1;
  }

  .mobile-pane-switch button {
    height: 40px;
    border: 0;
    border-bottom: 2px solid transparent;
    color: #6d7b77;
    background: transparent;
  }

  .mobile-pane-switch button.active {
    color: var(--primary-dark);
    border-bottom-color: var(--primary);
    font-weight: 600;
  }

  .image-container {
    display: block;
  }

  .image-pane {
    height: 100%;
  }

  .pane-label {
    display: none;
  }

  .image-stage {
    padding: 12px;
  }

  .docx-stage {
    padding: 8px;
  }

  .docx-preview-container /deep/ .docx-wrapper {
    padding: 8px !important;
  }
}
</style>
